import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { formatCurrency } from '../../utils/formatters';
import { API_BASE_URL } from '../../config';
import './embed.css';

const EmbedPrice = () => {
  const { ticker } = useParams();
  const [searchParams] = useSearchParams();

  // URL params for customization
  const theme = searchParams.get('theme') || 'dark';
  const bg = searchParams.get('bg') || 'transparent';
  const size = searchParams.get('size') || 'medium'; // small, medium, large, xlarge
  const showTicker = searchParams.get('ticker') !== 'false';
  const showChange = searchParams.get('change') !== 'false';
  const showName = searchParams.get('name') === 'true';
  const layout = searchParams.get('layout') || 'horizontal'; // horizontal, vertical, badge
  const refreshInterval = parseInt(searchParams.get('refresh')) || 5000;
  const animate = searchParams.get('animate') !== 'false';

  const [stockData, setStockData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [priceFlash, setPriceFlash] = useState(null); // 'up', 'down', or null
  const prevPriceRef = useRef(null);
  const socketRef = useRef(null);

  const fetchStock = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/stocks/ticker/${ticker}`);
      if (response.ok) {
        const data = await response.json();

        // Check for price change animation
        if (animate && prevPriceRef.current !== null && data.currentPrice !== prevPriceRef.current) {
          setPriceFlash(data.currentPrice > prevPriceRef.current ? 'up' : 'down');
          setTimeout(() => setPriceFlash(null), 500);
        }
        prevPriceRef.current = data.currentPrice;

        setStockData(data);
      }
    } catch (error) {
      console.error('Error fetching stock:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStock();

    // Set up socket for real-time updates
    socketRef.current = io(API_BASE_URL, {
      auth: { public: true }
    });

    socketRef.current.on('stock-updates', (updates) => {
      if (Array.isArray(updates)) {
        const update = updates.find(u => u.ticker?.toUpperCase() === ticker?.toUpperCase());
        if (update) {
          setStockData(prev => {
            if (!prev) return prev;

            const newPrice = update.currentPrice || update.price;
            if (animate && prevPriceRef.current !== null && newPrice !== prevPriceRef.current) {
              setPriceFlash(newPrice > prevPriceRef.current ? 'up' : 'down');
              setTimeout(() => setPriceFlash(null), 500);
            }
            prevPriceRef.current = newPrice;

            const basePrice = prev.basePrice || prev.currentPrice;
            const changePercent24h = basePrice > 0 ? ((newPrice - basePrice) / basePrice) * 100 : 0;
            return {
              ...prev,
              currentPrice: newPrice,
              changePercent24h: parseFloat(changePercent24h.toFixed(2)),
            };
          });
        }
      }
    });

    // Fallback polling
    const interval = setInterval(fetchStock, refreshInterval);

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
      clearInterval(interval);
    };
  }, [ticker, refreshInterval, animate]);

  if (loading) {
    return (
      <div className={`embed-price loading ${theme} size-${size}`} style={{ background: bg === 'transparent' ? 'transparent' : bg }}>
        <span className="price-loading">--</span>
      </div>
    );
  }

  if (!stockData) {
    return (
      <div className={`embed-price ${theme} size-${size}`} style={{ background: bg === 'transparent' ? 'transparent' : bg }}>
        {showTicker && <span className="price-ticker" style={{ color: '#555' }}>{ticker?.toUpperCase() || '---'}</span>}
        <span className="price-amount" style={{ color: '#555' }}>--</span>
      </div>
    );
  }

  const changePercent = stockData.changePercent24h || 0;
  const isPositive = changePercent >= 0;

  return (
    <div
      className={`embed-price ${theme} size-${size} layout-${layout} ${priceFlash ? `flash-${priceFlash}` : ''}`}
      style={{ background: bg === 'transparent' ? 'transparent' : bg }}
    >
      <button className="embed-fullscreen-btn embed-fullscreen-corner" onClick={() => document.documentElement.requestFullscreen?.()}>&#x26F6;</button>
      {showTicker && (
        <span className="price-ticker">{stockData.ticker}</span>
      )}

      {showName && (
        <span className="price-name">{stockData.name}</span>
      )}

      <span className={`price-value ${priceFlash ? `flash-${priceFlash}` : ''}`}>
        {formatCurrency(stockData.currentPrice)}
      </span>

      {showChange && (
        <span className={`price-change ${isPositive ? 'positive' : 'negative'}`}>
          {isPositive ? '▲' : '▼'} {isPositive ? '+' : ''}{changePercent.toFixed(2)}%
        </span>
      )}
    </div>
  );
};

export default EmbedPrice;
